import { zodResolver } from "@hookform/resolvers/zod";
import {
  CheckmarkIcon,
  CloseIcon,
  DropdownFormField,
  EditorIcon,
  IconButton,
  Input,
  InputField,
  notifyError,
  TrashcanIcon,
} from "@theprelude/ds";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

interface TopLevelViewGroup {
  name: string;
  displayName: string;
  views: Partial<Record<string, any>>;
}

interface TopLevelViewsSelectorProps {
  viewGroups: TopLevelViewGroup[];
  currentViewGroup: string | null;
  onSelect: (viewGroup: TopLevelViewGroup | null) => void;
  onUpdate: (viewGroup: TopLevelViewGroup, newName: string) => Promise<void>;
  onDelete: (viewGroup: TopLevelViewGroup) => Promise<void>;
  isPendingSave?: boolean;
  isPendingDelete?: boolean;
}

interface ViewNameEditorProps {
  initialName: string;
  onSave: (newName: string) => void;
  onCancel: () => void;
  isPendingSave?: boolean;
}

function ViewNameEditor({
  initialName,
  onSave,
  onCancel,
  isPendingSave,
}: ViewNameEditorProps) {
  const editorForm = useForm<{ newName: string }>({
    defaultValues: { newName: initialName || "" },
    resolver: zodResolver(z.object({ newName: z.string().min(1) })),
  });

  const onSubmit = (data: { newName: string }) => {
    const newNameTrimmed = data.newName.trim();
    if (!newNameTrimmed) {
      notifyError({ message: "View name cannot be empty." });
      return;
    }
    onSave(newNameTrimmed);
  };

  return (
    <FormProvider {...editorForm}>
      <form
        onSubmit={editorForm.handleSubmit(onSubmit)}
        className="flex items-center gap-2 px-4 pt-4"
      >
        <InputField
          className="w-[200px]"
          error={editorForm.formState.errors.newName?.message}
        >
          <Input
            {...editorForm.register("newName", {
              required: "Name cannot be empty",
            })}
            autoFocus
            className="h-[38px]"
            disabled={isPendingSave}
          />
        </InputField>
        <IconButton
          icon={<CheckmarkIcon />}
          type="submit"
          disabled={isPendingSave}
          loading={isPendingSave}
        />
        <IconButton
          icon={<CloseIcon />}
          onClick={onCancel}
          disabled={isPendingSave}
        />
      </form>
    </FormProvider>
  );
}

export function TopLevelViewsSelector({
  viewGroups,
  currentViewGroup,
  onSelect,
  onUpdate,
  onDelete,
  isPendingSave,
  isPendingDelete,
}: TopLevelViewsSelectorProps) {
  const [isEditingName, setIsEditingName] = useState(false);
  const form = useForm<{ savedView: string }>({
    defaultValues: { savedView: currentViewGroup || "" },
  });

  useEffect(() => {
    if (currentViewGroup) {
      form.setValue("savedView", currentViewGroup);
    }
  }, [currentViewGroup, form]);

  const selectedGroup = viewGroups.find((vg) => vg.name === currentViewGroup);

  const handleSelectView = (viewName: string | null) => {
    if (!viewName) {
      onSelect(null);
      return;
    }
    const viewGroup = viewGroups.find((vg) => vg.name === viewName);
    if (viewGroup) {
      onSelect(viewGroup);
    }
  };

  const handleSaveName = async (newName: string) => {
    if (selectedGroup) {
      await onUpdate(selectedGroup, newName);
      setIsEditingName(false);
    }
  };

  if (isEditingName && selectedGroup) {
    return (
      <ViewNameEditor
        initialName={selectedGroup.displayName}
        onSave={handleSaveName}
        onCancel={() => setIsEditingName(false)}
        isPendingSave={isPendingSave}
      />
    );
  }

  if (viewGroups.length === 0) {
    return null;
  }

  return (
    <FormProvider {...form}>
      <form className="flex items-center gap-2 px-4 pt-4">
        <DropdownFormField
          className="w-[200px]"
          contentClassName="w-[300px]"
          placeholder="Select a saved view"
          options={viewGroups.map((vg) => ({
            label: vg.displayName,
            value: vg.name,
          }))}
          {...form.register("savedView", {
            onChange: (e) => handleSelectView(e.target.value),
          })}
        />
        {selectedGroup && (
          <>
            <IconButton
              icon={<EditorIcon className="size-4" />}
              onClick={() => setIsEditingName(true)}
              disabled={isPendingSave}
            />
            <IconButton
              icon={<TrashcanIcon className="size-4" />}
              onClick={() => onDelete(selectedGroup)}
              disabled={isPendingDelete}
              loading={isPendingDelete}
            />
          </>
        )}
      </form>
    </FormProvider>
  );
}
